import { apartmentDetailText } from '../../i18n/pages/apartmentDetail';
import type { ApartmentDetailText } from '../../i18n/pages/apartmentDetail';
import type { Lang } from '../../i18n/types';
import { GroupKey, SectionKey, SECTION_GROUP, stripLeadingSymbols } from './groups';

/**
 * The index behind "Najít nastavení…". It is static and maintained by hand, which is its one cost:
 * a field renamed or removed without touching this file silently stops being findable.
 * `settingsIndex.test.ts` therefore checks every `targetId` against the page source.
 *
 * <p>Labels come from the page's own translations, so all four languages search in their own words;
 * the synonym lists (Czech and English, no diacritics needed) are what make "wifi", "heslo",
 * "kauce" or "odkaz" land on the right field whatever the UI language is.
 *
 * <p>The direct-booking web is deliberately absent: it is not released, so it must not be findable.
 */

export interface SettingsEntry {
  /** Unique, stable key (also the React key / option id). */
  id: string;
  section: SectionKey;
  group: GroupKey;
  /** DOM id of the field to land on. */
  targetId: string;
  /** Always-rendered neighbour for when `targetId` is behind a condition (door code, lock picker). */
  fallbackId?: string;
  label: (t: ApartmentDetailText) => string;
  synonyms: string[];
}

const e = (
  id: string,
  section: SectionKey,
  targetId: string,
  label: (t: ApartmentDetailText) => string,
  synonyms: string[],
  fallbackId?: string,
): SettingsEntry => ({ id, section, group: SECTION_GROUP[section], targetId, fallbackId, label, synonyms });

export const SETTINGS_INDEX: SettingsEntry[] = [
  // ── Základ ────────────────────────────────────────────────────────────────
  e('active', 'general', 'ae-isActive', t => t.fieldActive,
    ['aktivni', 'pozastavit', 'vypnout', 'deaktivovat', 'active', 'pause']),
  e('checkinLink', 'general', 'ae-checkinUrl', t => t.fieldCheckinUrl,
    ['odkaz', 'url', 'link', 'check-in', 'checkin', 'kopirovat', 'copy', 'zprava airbnb', 'booking']),
  e('internalName', 'general', 'ae-internalName', t => t.fieldInternalName,
    ['jmeno', 'nazev', 'name']),
  e('displayName', 'general', 'ae-displayName', t => t.fieldDisplayName,
    ['jmeno', 'nazev', 'host vidi', 'name']),
  e('address', 'general', 'ae-address', t => t.fieldAddress,
    ['ulice', 'misto', 'adresa', 'street']),
  e('capacity', 'general', 'ae-capacity', t => t.fieldCapacity,
    ['pocet hostu', 'lidi', 'osoby', 'guests', 'people']),
  e('description', 'general', 'ae-description', t => t.fieldDescription,
    ['popis', 'text']),
  e('checkinWindow', 'general', 'ae-publicCheckinWindowDays', t => t.fieldCheckinWindowDays,
    ['okno', 'kolik dni', 'predem', 'window', 'days ahead']),
  e('documentScan', 'general', 'ae-documentScan', t => t.documentScanLabel,
    ['sken', 'skenovat', 'pas', 'obcanka', 'doklad', 'passport', 'scan', 'mrz']),
  e('contactSource', 'contact', 'ae-contactSource', t => t.contactHeader,
    ['kontakt', 'uklizec', 'telefon', 'sprava', 'contact', 'cleaner']),
  e('contactFooter', 'contact', 'ae-guestFooterContactType', t => t.footerHeader,
    ['paticka', 'kontakt formular', 'footer']),
  e('deleteApartment', 'general', 'ae-deleteApartment', t => t.deleteApartment,
    ['smazat', 'odstranit', 'zrusit', 'delete', 'remove']),

  // ── Rezervace ─────────────────────────────────────────────────────────────
  e('calendar', 'calendar', 'sec-calendar', t => t.tabCalendar,
    ['kalendar', 'ical', 'airbnb', 'booking', 'synchronizace', 'sync', 'import', 'hostex', 'channel manager', 'api']),

  // ── Příjezd a průvodce ────────────────────────────────────────────────────
  e('guideEnabled', 'guide', 'guideEnabled', t => t.guideEnable,
    ['pruvodce', 'zapnout', 'aktivovat', 'guide', 'enable']),
  e('checkInTime', 'guide', 'ae-guide-checkInTime', t => t.guideFieldCheckinTime,
    ['cas', 'prijezd', 'check-in', 'checkin', 'hodina', 'time', 'arrival']),
  e('checkOutTime', 'guide', 'ae-guide-checkOutTime', t => t.guideFieldCheckoutTime,
    ['cas', 'odjezd', 'check-out', 'checkout', 'hodina', 'time', 'departure']),
  e('guideAddress', 'guide', 'ae-guide-address', t => `${t.guideFieldAddress} (${stripLeadingSymbols(t.tabGuide)})`,
    ['adresa', 'ulice', 'address']),
  e('guideAddressRemark', 'guide', 'ae-guide-addressRemark', t => t.guideFieldAddressRemark,
    ['jak najit', 'zvonek', 'poznamka', 'remark', 'doorbell']),
  e('keyAccessType', 'guide', 'ae-guide-keyAccessType', t => t.guideFieldAccessType,
    ['klic', 'klice', 'zamek', 'ttlock', 'chytry zamek', 'smart lock', 'schranka', 'keybox', 'pristup', 'dvere', 'key', 'access']),
  e('keyCode', 'guide', 'ae-guide-keyCode', t => t.guideFieldKeyCode,
    ['kod', 'heslo', 'pin', 'klic', 'schranka', 'door code', 'code'], 'ae-guide-keyAccessType'),
  e('keyRemark', 'guide', 'ae-guide-keyRemark', t => t.guideFieldKeyRemark,
    ['klic', 'poznamka', 'key']),
  e('wifiName', 'guide', 'ae-guide-wifiName', t => t.guideFieldWifiName,
    ['wifi', 'wi-fi', 'sit', 'internet', 'network']),
  e('wifiPassword', 'guide', 'ae-guide-wifiPassword', t => t.guideFieldWifiPassword,
    ['wifi', 'wi-fi', 'heslo', 'password', 'sit', 'internet']),
  e('parking', 'guide', 'ae-guide-parkingInfo', t => t.guideFieldParkingInfo,
    ['auto', 'parkovani', 'garaz', 'parking', 'car']),
  e('guideHostContact', 'guide', 'ae-guide-hostContactName', t => stripLeadingSymbols(t.guideSectionHostContact),
    ['telefon', 'email', 'kontakt', 'host', 'phone']),
  e('weather', 'guide', 'ae-guide-showWeather', t => stripLeadingSymbols(t.guideSecWeather),
    ['pocasi', 'weather']),
  e('detailedGuide', 'guide', 'ae-guide-detailedGuide', t => stripLeadingSymbols(t.guideSecDetailedGuide),
    ['pravidla', 'navody', 'odpad', 'spotrebice', 'rules', 'house rules']),
  e('attentionNotes', 'guide', 'ae-guide-attentionNotes', t => stripLeadingSymbols(t.guideSecAttentionNotes),
    ['upozorneni', 'klid', 'kourit', 'pozor', 'warning', 'quiet']),
  e('checkoutInstructions', 'guide', 'ae-guide-checkoutInstructions', t => stripLeadingSymbols(t.guideSecCheckoutInstructions),
    ['odjezd', 'checkout', 'klice vratit', 'check-out']),
  e('guidePhotos', 'guide', 'ae-guide-showPhotos', t => t.guideSectionPhotos,
    ['foto', 'fotka', 'obrazky', 'photos', 'pictures']),
  e('guideLinks', 'guide', 'ae-guide-showOtherLinks', t => t.guideSectionOtherLinks,
    ['odkaz', 'odkazy', 'url', 'web', 'links']),

  // ── Peníze ────────────────────────────────────────────────────────────────
  e('stripe', 'payments', 'sec-payments', t => t.paymentsHeader,
    ['stripe', 'platby', 'karta', 'ucet', 'platebni', 'kauce', 'priplatky', 'payments', 'account', 'card', 'deposit', 'upsell']),
  e('cityTaxCollection', 'citytax', 'ae-cityTaxCollection', t => stripLeadingSymbols(t.cityTaxCollectionLabel),
    ['poplatek', 'mistni poplatek', 'online', 'karta', 'city tax', 'tax']),
  e('cityTaxRate', 'citytax', 'ae-cityTaxAdult', t => t.fieldAdultRate,
    ['poplatek', 'mistni poplatek', 'lazensky', 'ubytovaci', 'sazba', 'taxa', 'mesto', 'nocni', 'city tax', 'rate']),
  e('cityTaxBank', 'citytax', 'ae-cityTaxBankAccount', t => t.fieldCityTaxBankAccount,
    ['poplatek', 'odvod', 'ucet', 'bank', 'iban']),
  e('cityTaxVariableSymbol', 'citytax', 'ae-cityTaxVariableSymbol', t => t.fieldCityTaxVariableSymbol,
    ['poplatek', 'vs', 'symbol', 'odvod']),
  e('cityTaxMailbox', 'citytax', 'ae-cityTaxMailboxId', t => t.fieldCityTaxMailbox,
    ['poplatek', 'datovka', 'datova schranka', 'schranka', 'urad', 'mailbox']),
  e('deposit', 'deposit', 'sec-deposit', t => stripLeadingSymbols(t.tabDeposit),
    ['kauce', 'depozit', 'deposit', 'zaloha', 'castka']),
  e('upsell', 'upsell', 'sec-upsell', t => stripLeadingSymbols(t.tabUpsell),
    ['hodiny', 'hodiny navic', 'early', 'late', 'prijezd', 'odjezd', 'upsell', 'cena', 'extra hours',
      'zavazadla', 'kufry', 'uschovna', 'batozina', 'luggage', 'bags']),

  // ── Úřady a export ────────────────────────────────────────────────────────
  e('ubyportUsername', 'ubyport', 'ae-ubyportUsername', t => t.fieldUbyportUsername,
    ['ubyport', 'prihlaseni', 'uby-ws', 'login', 'jmeno']),
  e('ubyportPassword', 'ubyport', 'ae-ubyportPassword', t => t.fieldUbyportPassword,
    ['ubyport', 'heslo', 'password']),
  e('idub', 'ubyport', 'ae-idub', t => t.fieldIdub,
    ['idub', 'ubyport', 'identifikator']),
  e('ubyportShortName', 'ubyport', 'ae-ubyportShortName', t => t.fieldShortName,
    ['zkratka', 'ubyport', 'short', 'umark']),
  e('hostFullName', 'ubyport', 'ae-hostFullName', t => t.fieldHostFullName,
    ['ubyport', 'hostitel', 'jmeno', 'host', 'name']),
  e('hostContact', 'ubyport', 'ae-hostPhone', t => t.fieldHostContact,
    ['ubyport', 'hostitel', 'telefon', 'email', 'phone']),
  e('autoSend', 'ubyport', 'ae-autoSendToUbyport', t => t.autoSendLabel,
    ['auto', 'automaticky', 'odesilani', 'odeslani', 'cizinci', 'policie', 'ubyport', 'submit']),
  // Behind the auto-send switch (not rendered while it is off, so the search lands on the switch) and
  // inside the closed "Advanced settings" fold, which jumpTo opens.
  e('autoSendMax', 'ubyport', 'ae-ubyportAutoSendMaxGuests', t => t.autoSendMaxLabel,
    ['auto', 'automaticky', 'limit', 'maximum', 'max', 'pocet hostu', 'vsichni hoste', 'cizinci', 'ubyport', 'cap', 'guests', 'pokrocile', 'advanced'],
    'ae-autoSendToUbyport'),
  e('autoSendDelay', 'ubyport', 'ae-ubyportAutoSendDelayDays', t => t.autoSendDelayLabel,
    ['auto', 'automaticky', 'zpozdeni', 'odlozit', 'den', 'dny', 'lhuta', 'cizinci', 'ubyport', 'delay', 'days', 'pokrocile', 'advanced'],
    'ae-autoSendToUbyport'),
  e('dorucenky', 'ubyport', 'dorucenka-download', t => t.dorucenkaHeader,
    ['dorucenka', 'dorucenky', 'zip', 'pdf', 'potvrzeni', 'stahnout', 'download', 'receipts']),
  e('export', 'export', 'sec-export', t => stripLeadingSymbols(t.exportHeader),
    ['export', 'csv', 'pdf', 'kniha hostu', 'kontrola', 'stahnout']),
];

/** Lower-case, no diacritics: "Místní" and "mistni" are the same word. */
export const normalizeText = (s: string): string =>
  s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

export const MAX_RESULTS = 8;

export interface SearchHit {
  entry: SettingsEntry;
  label: string;
}

/**
 * Rank 0: the label starts with the word. 1: a word of the label does. 2: the label contains it.
 * 3/4: only a synonym does (starts with / contains). `null`: no match.
 */
function tokenRank(token: string, label: string, synonyms: string[]): number | null {
  if (label.startsWith(token)) return 0;
  if (label.split(/[^\p{L}\p{N}]+/u).some(w => w.startsWith(token))) return 1;
  if (label.includes(token)) return 2;
  if (synonyms.some(s => s.startsWith(token))) return 3;
  if (synonyms.some(s => s.includes(token))) return 4;
  return null;
}

export function searchSettings(query: string, lang: Lang): SearchHit[] {
  const tokens = normalizeText(query).split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  const t = apartmentDetailText[lang];

  const scored: { hit: SearchHit; rank: number; order: number }[] = [];
  SETTINGS_INDEX.forEach((entry, order) => {
    const label = entry.label(t);
    const normLabel = normalizeText(label);
    const normSyn = entry.synonyms.map(normalizeText);
    let worst = 0;
    for (const token of tokens) {
      const r = tokenRank(token, normLabel, normSyn);
      if (r === null) return;
      worst = Math.max(worst, r);
    }
    scored.push({ hit: { entry, label }, rank: worst, order });
  });

  return scored
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .slice(0, MAX_RESULTS)
    .map(s => s.hit);
}
